import React, { useLayoutEffect, useRef, useState } from 'react';
import { AlertCircle, Save, SendHorizontal, Trash2 } from 'lucide-react';
import { formatMoney } from './invoiceTotals';

/**
 * The editor's action bar, pinned to the bottom of the viewport.
 *
 * It used to be a band at the top of the page, above ~3 500 px of form: by
 * the time the user had added rows it was out of view, and the reason
 * Publicera was disabled lived in a tooltip on a disabled button, which
 * browsers never show. The reason is text here, next to the button.
 *
 * Why fixed and not sticky: the app shell (`#main-wrapper`) is
 * `overflow: hidden`, which makes `position: sticky` resolve against a box
 * that never scrolls, so it never sticks. The bar is `position: fixed` and
 * takes its horizontal extent from an in-flow placeholder, so it stays
 * aligned with the form column when the sidebar collapses or the window
 * resizes.
 */
const InvoiceStickyBar = ({
    total = 0,
    currency = 'SEK',
    isTaxable = true,
    onSaveDraft,
    onPublish,
    onDelete,
    showDeleteButton = false,
    loading = false,
    hasUnsavedChanges = false,
    publishBlockedReason = null,
}) => {
    const blocked = Boolean(publishBlockedReason);
    const anchorRef = useRef(null);
    const barRef = useRef(null);
    const [frame, setFrame] = useState({ left: 0, width: 0, height: 0 });

    useLayoutEffect(() => {
        const measure = () => {
            const anchor = anchorRef.current;
            const bar = barRef.current;
            if (!anchor || !bar) return;
            const rect = anchor.getBoundingClientRect();
            setFrame({ left: rect.left, width: rect.width, height: bar.offsetHeight });
        };
        measure();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (observer && anchorRef.current) observer.observe(anchorRef.current);
        if (observer && barRef.current) observer.observe(barRef.current);
        window.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, []);

    return (
        <>
            {/* In-flow placeholder: reserves the bar's height so the last card
                is never hidden behind it, and gives the fixed bar its column. */}
            <div ref={anchorRef} className="inv-sticky-bar__anchor" style={{ height: frame.height || 64 }} aria-hidden="true" />

            {/* Fades the page out under the bar so cards do not show through
                the gap between the bar and the viewport edge while scrolling. */}
            <div
                className="inv-sticky-bar__backdrop"
                aria-hidden="true"
                style={frame.width ? { left: frame.left, width: frame.width } : undefined}
            />

            <div
                ref={barRef}
                className="inv-sticky-bar"
                role="region"
                aria-label="Fakturaåtgärder"
                style={frame.width ? { left: frame.left, width: frame.width } : undefined}
            >
                <div className="inv-sticky-bar__total">
                    <span className="inv-sticky-bar__total-label">
                        {isTaxable ? 'Totalt inkl. moms' : 'Totalt'}
                    </span>
                    <span className="inv-sticky-bar__total-value">{formatMoney(total, currency)}</span>
                </div>

                <div
                    className={`inv-sticky-bar__status${blocked ? ' inv-sticky-bar__status--warn' : ''}`}
                    aria-live="polite"
                    id="inv-publish-status"
                >
                    {blocked ? (
                        <>
                            <AlertCircle strokeWidth={2} />
                            <span>{publishBlockedReason}</span>
                        </>
                    ) : hasUnsavedChanges ? (
                        <span>Osparade ändringar</span>
                    ) : null}
                </div>

                <div className="inv-sticky-bar__actions">
                    {showDeleteButton && (
                        <button
                            type="button"
                            className="inv-btn inv-btn--danger"
                            onClick={onDelete}
                            disabled={loading}
                        >
                            <Trash2 strokeWidth={2} />
                            Radera utkast
                        </button>
                    )}
                    <button
                        type="button"
                        className="inv-btn inv-btn--secondary"
                        onClick={onSaveDraft}
                        disabled={loading}
                    >
                        <Save strokeWidth={2} />
                        Spara utkast
                    </button>
                    <button
                        type="button"
                        className="inv-btn inv-btn--primary"
                        onClick={onPublish}
                        disabled={blocked || loading}
                        aria-describedby={blocked ? 'inv-publish-status' : undefined}
                    >
                        <SendHorizontal strokeWidth={2} />
                        Publicera faktura
                    </button>
                </div>
            </div>
        </>
    );
};

export default InvoiceStickyBar;
